import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import type { KnowledgeNode } from "../types"
import useSysConfig from "@/hooks/useSysConfig"
import { useToast } from "@/hooks"
import { SOMETHING_WRONG_MSG } from "@/constants/errors"
import { API_PREFIX } from "@/constants"
import {
  AddFromNotionResponseSchema,
  makeCreateConnectionRequest,
  useCreateConnectionApi,
  useGetConnectionByIdApi,
} from "@/hooks/api"
import type { AddFromNotionRequest } from "@/hooks/api/knowledgeBase"
import type { Response } from "@/types"
import { hasPendingKnowledgeBaseStatus } from "./knowledgeBasePolling"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

function isWorkspaceNode(node: KnowledgeNode | null | undefined): boolean {
  return node?.ui_kind === "WORKSPACE"
}

function getKbIdForNode(node: KnowledgeNode | null | undefined): string | null {
  if (!node) return null
  if (isWorkspaceNode(node)) return null
  if ((node.knowledge_base_id || "").startsWith("kb-explorer:")) return null
  return node.knowledge_base_id || null
}

export interface UseNotionParams {
  root: KnowledgeNode
  findNodeById: (node: KnowledgeNode, id: string) => KnowledgeNode | null
  mergeChildren: (parentId: string, children: KnowledgeNode[]) => void
  setPendingNodeMap: React.Dispatch<
    React.SetStateAction<Map<string, { kbId: string; workspaceId: string }>>
  >
  getOrganizationIdForWorkspaceId: (workspaceId: string) => string | null
  listNodes: (params: {
    urlBuilderParams: { organizationId: string; workspaceId: string }
    searchParams?: URLSearchParams
  }) => Promise<Response<KnowledgeNode[]> | undefined>
  addFromNotion: (params: {
    urlBuilderParams: {
      organizationId: string
      workspaceId: string
      kbId: string
    }
    jsonBody: AddFromNotionRequest
  }) => Promise<Response<AddFromNotionResponseSchema> | undefined>
  refreshConnections: () => void
}

export interface UseNotionReturn {
  addFromNotionConnection: (
    folderId: string,
    connectionId: string
  ) => Promise<void>
  connectAnotherNotionWorkspace: (folderId: string) => Promise<void>
}

/**
 * useNotion (KB)
 *
 * Connection-based flow:
 * - Always open Notion OAuth UI in a popup (even for existing connections).
 * - Frontend polls /connections/:id until status=CONNECTED.
 * - Then calls POST /organizations/:org_id/workspaces/:workspace_id/knowledge_bases/:kb_id/files/import/notion
 *   with {parent_id, connection_id}.
 * - Backend imports ALL authorized pages server-side.
 */
export function useNotion({
  root,
  findNodeById,
  mergeChildren,
  setPendingNodeMap,
  getOrganizationIdForWorkspaceId,
  listNodes,
  addFromNotion,
  refreshConnections,
}: UseNotionParams): UseNotionReturn {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const sys = useSysConfig()

  const {
    data: createConnectionData,
    error: createConnectionError,
    fetchFn: createConnectionFetchFn,
  } = useCreateConnectionApi()
  // Keep API hook state internal; do not expose to outside UI.
  const { fetchFn: getConnectionFetchFn } = useGetConnectionByIdApi()
  const pollingSeqRef = useRef<Record<string, number>>({})

  const pollConnectionConnected = useCallback(
    async (
      connectionId: string,
      opts?: {
        statusBefore?: string | null
        lastConnectedAtBefore?: string | null
        updatedAtBefore?: string | null
      }
    ): Promise<boolean> => {
      const nextSeq = (pollingSeqRef.current[connectionId] ?? 0) + 1
      pollingSeqRef.current[connectionId] = nextSeq
      const seq = nextSeq
      const deadline = Date.now() + 300_000
      while (Date.now() < deadline) {
        if (pollingSeqRef.current[connectionId] !== seq) {
          return false
        }
        try {
          const connResp = await getConnectionFetchFn({
            urlBuilderParams: { connectionId },
          })
          const status = connResp?.data?.status
          const lastConnectedAt = connResp?.data?.last_connected_at ?? null
          const updatedAt = connResp?.data?.updated_at ?? null

          const wasConnected = (opts?.statusBefore || "") === "CONNECTED"
          // If we were not connected before, wait until CONNECTED.
          if (!wasConnected) {
            if (status === "CONNECTED") return true
            // Stop polling early if OAuth was canceled and the connection is still not connected
            if (
              status === "NOT_CONNECTED" &&
              opts?.updatedAtBefore !== null &&
              updatedAt !== (opts?.updatedAtBefore ?? null)
            ) {
              return false
            }
          } else {
            // For already-connected connections (Notion re-auth page selection), status may remain CONNECTED.
            // Use last_connected_at to detect success; updated_at change without last_connected_at
            // indicates a canceled popup.
            if (status === "CONNECTED") {
              const lastConnectedChanged =
                lastConnectedAt !== (opts?.lastConnectedAtBefore ?? null)
              const updatedAtChanged =
                updatedAt !== (opts?.updatedAtBefore ?? null)
              if (lastConnectedChanged) return true
              if (updatedAtChanged) return false
            }
          }
        } catch {
          // ignore and retry until deadline
        }
        await new Promise<void>((r) => window.setTimeout(r, 1000))
      }
      alertToast({ title: t(SOMETHING_WRONG_MSG) })
      return false
    },
    [alertToast, getConnectionFetchFn, t]
  )

  useEffect(() => {
    if (!createConnectionError) return
    console.error("[Notion] create connection failed", createConnectionError)
    alertToast({
      ...getUserFacingApiErrorToastProps(createConnectionError, t, {
        title: t(SOMETHING_WRONG_MSG),
        fallbackDescription: t(SOMETHING_WRONG_MSG),
      }),
    })
  }, [alertToast, createConnectionError, t])

  const importAllAuthorizedPagesToFolder = useCallback(
    async (folderId: string, connectionId: string) => {
      const folder = findNodeById(root, folderId)
      if (!folder || isWorkspaceNode(folder)) return
      const workspaceId = folder.workspace_id
      if (!workspaceId) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return
      const kbId = getKbIdForNode(folder)
      if (!kbId) return

      const resp = await addFromNotion({
        urlBuilderParams: { organizationId, workspaceId, kbId },
        jsonBody: {
          parent_id: folderId,
          connection_id: Number(connectionId),
        },
      })

      const created = resp?.data?.nodes || []
      const nodesResp = await listNodes({
        urlBuilderParams: { organizationId, workspaceId },
        searchParams: new URLSearchParams({
          parent_id: folderId,
        }),
      })
      const children = (nodesResp?.data || []).filter(
        (c) => c.node_type === "FILE"
      )
      mergeChildren(
        folderId,
        children.map((c) => ({
          ...c,
          parent_id: folderId,
          children: [],
          children_loaded: false,
          ui_kind: "NODE" as const,
          workspace_id: workspaceId,
        }))
      )

      setPendingNodeMap((prev) => {
        const next = new Map(prev)
        for (const n of created) {
          if (hasPendingKnowledgeBaseStatus(n.status))
            next.set(n.id, { kbId, workspaceId })
        }
        return next
      })
    },
    [
      addFromNotion,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      listNodes,
      mergeChildren,
      root,
      setPendingNodeMap,
    ]
  )

  const addFromNotionConnection = useCallback(
    async (folderId: string, connectionId: string) => {
      // Always reopen Notion UI so user can re-select pages each time.
      let statusBefore: string | null = null
      let lastConnectedAtBefore: string | null = null
      let updatedAtBefore: string | null = null
      try {
        const connResp = await getConnectionFetchFn({
          urlBuilderParams: { connectionId },
        })
        statusBefore = connResp?.data?.status ?? null
        lastConnectedAtBefore = connResp?.data?.last_connected_at ?? null
        updatedAtBefore = connResp?.data?.updated_at ?? null
      } catch (e) {
        console.error("[Notion] prefetch connection failed", e)
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return
      }

      const connectUrl = `${sys.serverRoot}${API_PREFIX}/connections/${connectionId}/connect?mode=popup`
      window.open(connectUrl, "codeer-oauth", "popup,width=520,height=720")

      const ok = await pollConnectionConnected(connectionId, {
        statusBefore,
        lastConnectedAtBefore,
        updatedAtBefore,
      })
      if (!ok) return

      await importAllAuthorizedPagesToFolder(folderId, connectionId)
    },
    [
      alertToast,
      importAllAuthorizedPagesToFolder,
      getConnectionFetchFn,
      pollConnectionConnected,
      sys.serverRoot,
      t,
    ]
  )

  const connectAnotherNotionWorkspace = useCallback(
    async (folderId: string) => {
      let resp: Awaited<ReturnType<typeof createConnectionFetchFn>>
      try {
        resp = await createConnectionFetchFn({
          jsonBody: makeCreateConnectionRequest(
            `Notion (${new Date().toISOString()})`,
            "NOTION"
          ),
        })
      } catch (e) {
        console.error("[Notion] create connection failed", e)
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return
      }
      const created = resp?.data ?? createConnectionData?.data
      if (!created?.id) return

      refreshConnections()

      const newConnId = String(created.id)

      let statusBefore: string | null = null
      let lastConnectedAtBefore: string | null = null
      let updatedAtBefore: string | null = null
      try {
        const connResp = await getConnectionFetchFn({
          urlBuilderParams: { connectionId: newConnId },
        })
        statusBefore = connResp?.data?.status ?? null
        lastConnectedAtBefore = connResp?.data?.last_connected_at ?? null
        updatedAtBefore = connResp?.data?.updated_at ?? null
      } catch (e) {
        console.error("[Notion] prefetch connection failed", e)
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return
      }

      // Open provider connect URL directly
      const connectUrl = `${sys.serverRoot}${API_PREFIX}/connections/${newConnId}/connect?mode=popup`
      window.open(connectUrl, "codeer-oauth", "popup,width=520,height=720")

      const ok = await pollConnectionConnected(newConnId, {
        statusBefore,
        lastConnectedAtBefore,
        updatedAtBefore,
      })
      if (!ok) return

      // Refresh connections once to update the connection list in the dropdown
      refreshConnections()

      await importAllAuthorizedPagesToFolder(folderId, newConnId)
    },
    [
      alertToast,
      createConnectionData,
      createConnectionFetchFn,
      getConnectionFetchFn,
      importAllAuthorizedPagesToFolder,
      pollConnectionConnected,
      refreshConnections,
      sys.serverRoot,
      t,
    ]
  )

  return {
    addFromNotionConnection,
    connectAnotherNotionWorkspace,
  }
}
